/**
 * "Activity" — four sub-tabs: what the machine did (repo runs), what THIS
 * device did (session log), the commit history of main, and a status overview.
 */

import { useEffect, useState } from "react";
import { actorLabel, relativeTime, type ActivityEntry } from "../lib/activity";
import {
  latestWorkflowRun,
  listCommits,
  listOpenNightlyPrs,
  listWorkflowRuns,
  type CommitInfo,
  type NightlyPr,
  type RepoConfig,
  type WorkflowRun,
} from "../lib/github";
import type { Item } from "../lib/item";
import { clearLog, logEvent, sessionLog } from "../lib/log";
import { MODE_LABELS, type NodeMode } from "../lib/nodes";
import { isSnoozed, type Question } from "../lib/questions";

type Sub = "runs" | "session" | "commits" | "overview";

const SUBS: [Sub, string][] = [
  ["runs", "Runs"],
  ["session", "Session log"],
  ["commits", "Commits"],
  ["overview", "Overview"],
];

export function Activity({
  entries,
  loading,
  config,
  questions,
  items,
  modes,
  stagedCount,
  onRefresh,
  onFullReload,
}: {
  entries: ActivityEntry[];
  loading: boolean;
  config: RepoConfig | null;
  questions: Question[];
  items: Item[];
  modes: ReadonlyMap<string, NodeMode>;
  stagedCount: number;
  onRefresh: () => void;
  onFullReload: () => void;
}) {
  const [sub, setSub] = useState<Sub>("runs");
  const [, setTick] = useState(0);

  const [commits, setCommits] = useState<CommitInfo[] | null>(null);
  const [commitsError, setCommitsError] = useState("");
  const [lastRun, setLastRun] = useState<WorkflowRun | null | undefined>(undefined);
  const [runs, setRuns] = useState<WorkflowRun[] | null>(null);
  const [runsError, setRunsError] = useState("");
  const [strands, setStrands] = useState<NightlyPr[]>([]);

  useEffect(() => {
    if (sub !== "commits" || !config || commits !== null) return;
    let alive = true;
    void (async () => {
      try {
        const list = await listCommits(config);
        if (alive) setCommits(list);
      } catch (e) {
        if (alive) setCommitsError(e instanceof Error ? e.message : "failed to load");
      }
    })();
    return () => {
      alive = false;
    };
  }, [sub, config, commits]);

  useEffect(() => {
    if (sub !== "overview" || !config || lastRun !== undefined) return;
    let alive = true;
    void (async () => {
      try {
        const run = await latestWorkflowRun(config, "nightly_agents.yml");
        if (alive) setLastRun(run);
      } catch {
        if (alive) setLastRun(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [sub, config, lastRun]);

  // Runs tab: read the Actions API, NOT activity.jsonl — a failed run merges
  // nothing, so the file feed can never show it (that is exactly how two
  // stranded runs went unnoticed for days, 2026-09-24).
  useEffect(() => {
    if (sub !== "runs" || !config || runs !== null) return;
    let alive = true;
    void (async () => {
      try {
        const list = await listWorkflowRuns(config, "nightly_agents.yml", 8);
        if (alive) setRuns(list);
      } catch (e) {
        if (alive) {
          setRuns([]);
          setRunsError(e instanceof Error ? e.message : "failed to load runs");
        }
      }
      try {
        const prs = await listOpenNightlyPrs(config);
        if (alive) setStrands(prs);
      } catch {
        // The runs list already carries the state; strands stay unknown.
      }
    })();
    return () => {
      alive = false;
    };
  }, [sub, config, runs]);

  const log = [...sessionLog()].reverse();

  return (
    <div className="mx-auto w-full max-w-2xl px-4 pb-32 pt-4">
      <div className="mb-3 flex items-center justify-between">
        <h1 className="text-xl font-semibold text-ink-100">Activity</h1>
        <span className="flex items-center gap-1.5">
          <button onClick={onRefresh} className="btn-ghost px-3 py-1.5 text-xs">
            {loading ? "loading…" : "⟳ Refresh"}
          </button>
          <button
            onClick={onFullReload}
            className="btn-ghost px-3 py-1.5 text-xs"
            title="Forget everything loaded and fetch the full tree again"
          >
            Full reload
          </button>
        </span>
      </div>

      <div className="mb-4 flex flex-wrap gap-1.5">
        {SUBS.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setSub(key)}
            className={`chip ${
              sub === key ? "bg-accent/15 text-accent-soft" : "bg-ink-850 text-ink-400"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {sub === "runs" && (
        <>
          {strands.length > 0 && (
            <div className="card mb-3 border border-danger/40 p-3">
              <div className="text-sm font-medium text-danger">
                {strands.length} stranded run PR{strands.length === 1 ? "" : "s"}
              </div>
              <div className="mt-1 text-xs leading-relaxed text-ink-400">
                A failed run merges nothing, so the feed below still ends at the last
                successful night. The drafts wait on these branches:
              </div>
              <div className="mt-2 space-y-1.5">
                {strands.map((pr) => (
                  <a
                    key={pr.number}
                    href={pr.htmlUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 text-xs text-accent-soft underline underline-offset-2"
                  >
                    <span className="shrink-0">#{pr.number}</span>
                    <span className="min-w-0 flex-1 truncate text-ink-300">{pr.headRef}</span>
                    <span className="shrink-0 text-ink-500">{relativeTime(pr.createdAt)}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="card mb-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wide text-ink-500">
                Runs (GitHub Actions)
              </span>
              <button
                className="btn-ghost px-2 py-1 text-[11px]"
                onClick={() => {
                  setRuns(null);
                  setStrands([]);
                  setRunsError("");
                }}
              >
                ⟳
              </button>
            </div>
            {runsError ? (
              <div className="text-xs text-danger">{runsError}</div>
            ) : runs === null ? (
              <div className="text-xs text-ink-500">
                {config ? "loading runs…" : "Connect a repo to see runs."}
              </div>
            ) : runs.length === 0 ? (
              <div className="text-xs text-ink-500">No runs found.</div>
            ) : (
              <div className="space-y-1.5">
                {runs.map((run) => (
                  <div key={run.id} className="flex items-center gap-2 text-xs">
                    <span className={`chip shrink-0 ${runStatusChip(run)}`}>
                      {runStatusLabel(run)}
                    </span>
                    <span className="shrink-0 text-ink-500">#{run.runNumber}</span>
                    <span className="shrink-0 text-ink-600">{eventLabel(run.event)}</span>
                    <span className="min-w-0 flex-1 truncate text-ink-500">
                      {relativeTime(run.createdAt)}
                    </span>
                    <span className="shrink-0 text-ink-600">{runDurationLabel(run)}</span>
                    <a
                      href={run.htmlUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="shrink-0 text-accent-soft underline underline-offset-2"
                    >
                      open ↗
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-ink-500">
            Conductor feed
          </div>
          {entries.length === 0 ? (
            <div className="card p-6 text-center text-sm text-ink-400">
              Nothing yet — the next run writes here.
            </div>
          ) : (
            <div className="space-y-2">
              {entries.map((e, idx) => (
                <div key={`${e.ts}-${idx}`} className="card p-3">
                  <div className="flex items-center gap-2">
                    <span className={`chip ${actorStyle(e.actor)}`}>{actorLabel(e.actor)}</span>
                    <span className="min-w-0 flex-1 truncate text-sm text-ink-200">
                      {e.action}
                    </span>
                    <span className="shrink-0 text-xs text-ink-500">{relativeTime(e.ts)}</span>
                  </div>
                  {e.detail && (
                    <div className="mt-1.5 text-xs leading-relaxed text-ink-500">
                      {e.detail}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {sub === "session" && (
        <>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wide text-ink-500">
              This session (device)
            </span>
            <button
              className="btn-ghost px-2 py-1 text-[11px]"
              onClick={() => {
                clearLog();
                logEvent("info", "log cleared");
                setTick((t) => t + 1);
              }}
            >
              Clear
            </button>
          </div>
          {log.length === 0 ? (
            <div className="card p-3 text-xs text-ink-500">
              Nothing logged yet — loads, staged edits and confirms appear here.
            </div>
          ) : (
            <div className="card divide-y divide-ink-850">
              {log.slice(0, 50).map((e, idx) => (
                <div key={`${e.ts}-${idx}`} className="flex items-start gap-2 p-2.5">
                  <span
                    className={`mt-1 inline-block h-2 w-2 shrink-0 rounded-full ${
                      e.kind === "ok"
                        ? "bg-accent"
                        : e.kind === "warn"
                          ? "bg-warn"
                          : e.kind === "error"
                            ? "bg-danger"
                            : "bg-ink-600"
                    }`}
                  />
                  <span className="min-w-0 flex-1 text-xs leading-relaxed text-ink-300">
                    {e.text}
                  </span>
                  <span className="shrink-0 text-[11px] text-ink-600">
                    {relativeTime(e.ts)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {sub === "commits" && (
        <>
          {commitsError ? (
            <div className="card p-3 text-xs text-danger">{commitsError}</div>
          ) : commits === null ? (
            <div className="card p-3 text-xs text-ink-500">
              {config ? "loading commits…" : "Connect a repo to see the commit history."}
            </div>
          ) : (
            <div className="space-y-1.5">
              {commits.map((c) => (
                <div key={c.sha} className="card flex items-center gap-2 p-2.5">
                  <span className="chip shrink-0 bg-ink-850 font-mono text-[10px] text-ink-500">
                    {c.sha.slice(0, 7)}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-xs text-ink-300">
                    {c.message}
                  </span>
                  <span className="shrink-0 text-[11px] text-ink-600">
                    {relativeTime(c.date)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {sub === "overview" && (
        <Overview
          questions={questions}
          items={items}
          modes={modes}
          stagedCount={stagedCount}
          lastRun={lastRun}
          hasRepo={config !== null}
        />
      )}
    </div>
  );
}

function Overview({
  questions,
  items,
  modes,
  stagedCount,
  lastRun,
  hasRepo,
}: {
  questions: Question[];
  items: Item[];
  modes: ReadonlyMap<string, NodeMode>;
  stagedCount: number;
  lastRun: WorkflowRun | null | undefined;
  hasRepo: boolean;
}) {
  const open = questions.filter((q) => q.status === "open" && !isSnoozed(q));
  const snoozed = questions.filter((q) => q.status === "open" && isSnoozed(q));
  const decided = questions.filter((q) => q.status !== "open");

  const domains = [...new Set(questions.map((q) => q.domain))].sort();
  const perDomain = domains.map((d) => ({
    domain: d,
    open: open.filter((q) => q.domain === d).length,
    decided: decided.filter((q) => q.domain === d).length,
  }));

  const activeModes = [...modes.entries()].sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-2">
        <Stat label="Open" value={open.length} />
        <Stat label="Snoozed" value={snoozed.length} />
        <Stat label="Decided" value={decided.length} />
        <Stat label="Staged" value={stagedCount} />
      </div>

      <div className="card p-3">
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-500">
          Last nightly run
        </div>
        {lastRun === undefined ? (
          <div className="text-xs text-ink-500">
            {hasRepo ? "checking…" : "Connect a repo to see run status."}
          </div>
        ) : lastRun === null ? (
          <div className="text-xs text-ink-500">no runs yet</div>
        ) : (
          <div className="flex items-center gap-2 text-sm">
            <span
              className={`chip ${
                lastRun.conclusion === "success"
                  ? "bg-accent/15 text-accent-soft"
                  : lastRun.conclusion
                    ? "bg-danger/15 text-danger"
                    : "bg-warn/15 text-warn"
              }`}
            >
              {lastRun.conclusion ?? lastRun.status}
            </span>
            <span className="text-ink-400">#{lastRun.runNumber}</span>
            <span className="min-w-0 flex-1 truncate text-xs text-ink-500">
              {relativeTime(lastRun.createdAt)}
            </span>
            <a
              href={lastRun.htmlUrl}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 text-xs text-accent-soft underline underline-offset-2"
            >
              open ↗
            </a>
          </div>
        )}
      </div>

      <div className="card divide-y divide-ink-850">
        <div className="grid grid-cols-[1fr_auto_auto] gap-3 p-3 text-xs font-medium uppercase tracking-wide text-ink-500">
          <span>Domain</span>
          <span className="w-12 text-right">Open</span>
          <span className="w-14 text-right">Decided</span>
        </div>
        {perDomain.map((r) => (
          <div key={r.domain} className="grid grid-cols-[1fr_auto_auto] gap-3 p-3 text-sm">
            <span className="min-w-0 truncate capitalize text-ink-200">{r.domain}</span>
            <span className="w-12 text-right text-ink-300">{r.open}</span>
            <span className="w-14 text-right text-ink-500">{r.decided}</span>
          </div>
        ))}
      </div>

      <div className="card p-3">
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-500">
          Steered areas ({activeModes.length})
        </div>
        {activeModes.length === 0 ? (
          <div className="text-xs text-ink-500">
            All areas run on Standard — no modes set.
          </div>
        ) : (
          <div className="space-y-1.5">
            {activeModes.map(([node, mode]) => (
              <div key={node} className="flex items-center gap-2 text-sm">
                <span className="min-w-0 flex-1 truncate text-ink-300">{node}</span>
                <span className="chip shrink-0 bg-ink-800 text-ink-300">
                  {MODE_LABELS[mode]}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card p-3 text-xs text-ink-500">
        {items.length} item{items.length === 1 ? "" : "s"} · {questions.length} questions
      </div>
    </div>
  );
}

/** Chip classes for a run row: success green, failure red, running/queued warn. */
function runStatusChip(run: WorkflowRun): string {
  if (run.status !== "completed") return "bg-warn/15 text-warn";
  return run.conclusion === "success"
    ? "bg-accent/15 text-accent-soft"
    : "bg-danger/15 text-danger";
}

function runStatusLabel(run: WorkflowRun): string {
  if (run.status !== "completed") return run.status;
  return run.conclusion ?? run.status;
}

/** "42 min" for a finished run; empty while it is still running. */
function runDurationLabel(run: WorkflowRun): string {
  if (run.status !== "completed" || !run.createdAt || !run.updatedAt) return "";
  const ms = new Date(run.updatedAt).getTime() - new Date(run.createdAt).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return "";
  const mins = Math.max(1, Math.round(ms / 60000));
  return `${mins} min`;
}

function eventLabel(event: string): string {
  return event === "schedule" ? "cron" : event === "workflow_dispatch" ? "manual" : event;
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="card p-3 text-center">
      <div className="text-lg font-semibold text-ink-100">{value}</div>
      <div className="text-[11px] text-ink-500">{label}</div>
    </div>
  );
}

function actorStyle(actor: string): string {
  switch (actor) {
    case "nightly":
      return "bg-accent/15 text-accent-soft";
    case "conductor":
      return "bg-ink-800 text-ink-200";
    case "consolidator":
    case "reviewer":
      return "bg-warn/15 text-warn";
    case "console":
      return "bg-ink-850 text-ink-300";
    default:
      return "bg-ink-850 text-ink-400";
  }
}
